import { isEqual, sortBy } from "lodash";
import { Position } from "react-flow-renderer";
import { InsightInputType, OptimizationParameterParameterType, ScenarioNodeType, } from "../rs-workspace.types";
import { convertToCSV, getTextContentFromHtml, numberToFixedPointNotation } from "utils/string";
import { SpaceviewerAxisType, TimeUnits, } from "shared/types";
import { getTimePartsFromSeconds, getTimeUnit, toZeroPrefixNotation } from "utils/dateTime";
import { isNotEmpty } from "utils/validate";
import { getArrayMinMax } from "utils/data";
import { findDictionaryItemByValue, recipeParameterUnitsType } from "shared/rsDictionary";
import { DELETE_SCENARIO_ENTITY_MESSAGES } from "shared/labels";
import { getFieldRestrictions, getRestrictionDefaultValue, } from "../../knowledge-base-item/knowledge-base-item.utils";
import { InsightFormFields } from "pages/RS-Workspace/components/Scenario/components/InsightDialog";
import { TimeFrameFormFields } from "pages/RS-Workspace/components/Scenario/components/InsightDialog/components/Output/components/OutputContent/components/TimeframeDialog";
import { ParameterFormFields } from "pages/RS-Workspace/components/Scenario/components/OptimizationDialog/components/ParametersToOptimize";
import { TimingFormFields } from "rs-components/TimingFields";
import { ParameterTypesCategoryType, RecipeStepType, } from "../../recipe/recipe.types";
import { parseComplexUnitToHtml } from "store/recipe/custom-variables.utils";
import { HighlightEntityType } from "store/knowledge-base-item/knowledge-base-item.types";
import { isOptimizationCompleted } from "./completeness.utils";
import { OUTPUT_FINAL_ID } from "../selectors/rs-workspace.selectors";
import { getInsightOutputOptions } from "./insights.utils";
import { flatSteps } from "../../recipe/recipe.utils";
import { getOptimizationParameterSectionTargetSource } from "./common.utils";
const CELL_MODEL_ITEM_HEIGHT = 48;
const BOTTOM_MARGIN = 16;
const NODE_SIDE_MARGIN = 16;
const PLUS_BUTTON_HEIGHT = 32;
const PLUS_BUTTON_TOP_MARGIN = 16;
const SECTION_PADDING = 8;
const RECIPE_ITEM_HEIGHT = 48;
const RECIPE_SUBSECTION_BOTTOM_BORDER_HEIGHT = 4;
const PROCESS_DATA_ITEM_HEIGHT = 72;
const SIMULATION_ITEM_HEIGHT = 96;
const INSIGHT_ITEM_HEIGHT = 88;
const OPTIMIZATION_ITEM_HEIGHT = INSIGHT_ITEM_HEIGHT;
export const SECTION_SUBHEADER_HEIGHT = 49;
export const PAGE_HEADERS_HEIGHT = 165;
export const PREVIEW_LINE_COLORS = ["#00B9BE", "#E6007D", "#E9D800"];
export const INSIGHT_INPUT_COLORS = ["#F06EA5", "#008796", "#00A0E6"];
export const defaultInputSetInput = {
    inputId: null,
    id: 1,
    inputType: InsightInputType.SIMULATION,
    adjustmentId: null,
    cutInfo: null,
};
const getDefaultTimeFrameCut = (restrictions) => ({
    [TimingFormFields.timestampType]: getRestrictionDefaultValue(restrictions, [
        TimingFormFields.timestampType,
    ]),
    [TimingFormFields.offset]: getRestrictionDefaultValue(restrictions, [TimingFormFields.offset]),
    [TimingFormFields.offsetUnit]: getRestrictionDefaultValue(restrictions, [TimingFormFields.offsetUnit]),
});
export const getDefaultTimeFrame = (restrictions) => {
    const type = getRestrictionDefaultValue(restrictions, [`${InsightFormFields.timeFrame}.Type`]);
    const restrictionsByType = getFieldRestrictions(restrictions, [InsightFormFields.timeFrame, type]);
    return {
        [TimeFrameFormFields.type]: type,
        [TimeFrameFormFields.start]: getDefaultTimeFrameCut(getFieldRestrictions(restrictionsByType, [TimeFrameFormFields.start])),
        [TimeFrameFormFields.end]: getDefaultTimeFrameCut(getFieldRestrictions(restrictionsByType, [TimeFrameFormFields.end])),
    };
};
const getMinCellModelsRecipesHeight = (availableHeight) => (availableHeight - SECTION_SUBHEADER_HEIGHT) / 2 - RECIPE_SUBSECTION_BOTTOM_BORDER_HEIGHT / 2;
const getCellModelsRecipesHeightBasedOnOneOfSection = (availableHeight, oneOfSectionHeight) => availableHeight - oneOfSectionHeight - SECTION_SUBHEADER_HEIGHT - RECIPE_SUBSECTION_BOTTOM_BORDER_HEIGHT;
export const calculateScenarioCellModelsRecipesSectionHeight = (cellModels, recipes, availableHeight) => {
    const minCellModelsRecipesHeight = getMinCellModelsRecipesHeight(availableHeight);
    const requiredCellModelsHeight = (CELL_MODEL_ITEM_HEIGHT + BOTTOM_MARGIN) * cellModels.length +
        PLUS_BUTTON_HEIGHT +
        PLUS_BUTTON_TOP_MARGIN +
        SECTION_PADDING * 2;
    const requiredRecipesHeight = (RECIPE_ITEM_HEIGHT + BOTTOM_MARGIN) * recipes.length +
        PLUS_BUTTON_HEIGHT +
        PLUS_BUTTON_TOP_MARGIN +
        SECTION_PADDING * 2;
    const requiredCellModelsRecipesRequiredSectionHeight = requiredCellModelsHeight + requiredRecipesHeight;
    if (requiredCellModelsRecipesRequiredSectionHeight >= availableHeight) {
        return {
            cellModelsSectionHeight: requiredCellModelsHeight,
            recipesSectionHeight: requiredRecipesHeight,
            cellModelsRecipesSectionHeight: requiredCellModelsRecipesRequiredSectionHeight,
        };
    }
    else {
        if (requiredCellModelsHeight > minCellModelsRecipesHeight) {
            return {
                cellModelsSectionHeight: requiredCellModelsHeight,
                recipesSectionHeight: getCellModelsRecipesHeightBasedOnOneOfSection(availableHeight, requiredCellModelsHeight),
                cellModelsRecipesSectionHeight: availableHeight,
            };
        }
        else if (requiredRecipesHeight > minCellModelsRecipesHeight) {
            return {
                cellModelsSectionHeight: getCellModelsRecipesHeightBasedOnOneOfSection(availableHeight, requiredRecipesHeight),
                recipesSectionHeight: requiredRecipesHeight,
                cellModelsRecipesSectionHeight: availableHeight,
            };
        }
        else {
            return {
                cellModelsSectionHeight: minCellModelsRecipesHeight,
                recipesSectionHeight: minCellModelsRecipesHeight,
                cellModelsRecipesSectionHeight: availableHeight,
            };
        }
    }
};
const calculateScenarioSimulationsSectionHeight = (simulations, availableHeight) => {
    const requiredHeight = (SIMULATION_ITEM_HEIGHT + BOTTOM_MARGIN) * simulations.length;
    return requiredHeight > availableHeight ? requiredHeight : availableHeight;
};
export const calculateScenarioInsightsSectionHeight = (insights, availableHeight) => {
    const requiredHeight = (INSIGHT_ITEM_HEIGHT + BOTTOM_MARGIN) * insights.length;
    return requiredHeight > availableHeight ? requiredHeight : availableHeight;
};
export const calculateScenarioOptimizationsSectionHeight = (optimizations, availableHeight) => {
    const requiredHeight = (OPTIMIZATION_ITEM_HEIGHT + BOTTOM_MARGIN) * optimizations.length;
    return requiredHeight > availableHeight ? requiredHeight : availableHeight;
};
const calculateScenarioCellModelsSectionTopPadding = (sectionHeight, cellModels) => {
    const contentHeight = (CELL_MODEL_ITEM_HEIGHT + BOTTOM_MARGIN) * cellModels.length + PLUS_BUTTON_HEIGHT;
    return (sectionHeight - contentHeight) / 2;
};
const calculateScenarioRecipesSectionTopPadding = (sectionHeight, recipes) => {
    const contentHeight = (RECIPE_ITEM_HEIGHT + BOTTOM_MARGIN) * recipes.length + PLUS_BUTTON_HEIGHT;
    return (sectionHeight - contentHeight) / 2 + RECIPE_SUBSECTION_BOTTOM_BORDER_HEIGHT;
};
const calculateCenteredSectionTopPadding = (sectionHeight, itemsCount, itemHeight) => {
    const contentHeight = (itemHeight + BOTTOM_MARGIN) * itemsCount;
    return (sectionHeight - contentHeight) / 2 + BOTTOM_MARGIN / 2;
};
export const isDefaultVariantSuccessfullyCalculated = (simulationId, persistedPreviews) => {
    const persistedPreviewData = persistedPreviews?.[`${simulationId}_${0}`];
    return !!persistedPreviewData?.graphData && !persistedPreviewData?.graphData?.errorMessage;
};
export const isVariantErrored = (simulationId, variantId, persistedPreviews) => {
    const persistedPreviewData = persistedPreviews?.[`${simulationId}_${variantId}`];
    return persistedPreviews === null ? false : !!persistedPreviewData?.graphData?.errorMessage;
};
export const positionScenarioCellModels = (cellModels, recipes, widgetWidth, availableHeight, highlightedIds) => {
    const cellModelsData = [];
    const { cellModelsSectionHeight } = calculateScenarioCellModelsRecipesSectionHeight(cellModels, recipes, availableHeight);
    const topPadding = calculateScenarioCellModelsSectionTopPadding(cellModelsSectionHeight, cellModels);
    cellModels.forEach(({ id, name, isCompleted: isComplete }, index) => {
        const previousPreparedNode = cellModelsData[index - 1];
        const yPosition = !!previousPreparedNode && !!previousPreparedNode.data
            ? previousPreparedNode.data.bottomYPos + BOTTOM_MARGIN
            : topPadding;
        cellModelsData.push({
            id: `c${id}`,
            type: ScenarioNodeType.CELLMODEL,
            position: { x: 0, y: yPosition },
            data: {
                id: `c${id}`,
                originalId: id,
                name,
                bottomYPos: yPosition + CELL_MODEL_ITEM_HEIGHT,
                internalType: ScenarioNodeType.CELLMODEL,
                nodeWidth: widgetWidth / 4 - 64,
                shouldHighlightNode: highlightedIds.includes(id),
                isComplete,
            },
            sourcePosition: Position.Left,
            targetPosition: Position.Right,
        });
    });
    return cellModelsData;
};
export const positionScenarioRecipes = (recipes, cellModels, widgetWidth, availableHeight, highlightedIds) => {
    const { cellModelsSectionHeight, recipesSectionHeight } = calculateScenarioCellModelsRecipesSectionHeight(cellModels, recipes, availableHeight);
    const topPadding = calculateScenarioRecipesSectionTopPadding(recipesSectionHeight, recipes);
    const recipeItemsStartingPoint = cellModelsSectionHeight + SECTION_SUBHEADER_HEIGHT + topPadding;
    const recipesData = [];
    recipes.forEach(({ id, name, isCompleted: isComplete }, index) => {
        const previousPreparedNode = recipesData[index - 1];
        const yPosition = !!previousPreparedNode && previousPreparedNode.data
            ? previousPreparedNode.data.bottomYPos + BOTTOM_MARGIN
            : recipeItemsStartingPoint;
        recipesData.push({
            id: `r${id}`,
            type: ScenarioNodeType.RECIPE,
            position: { x: 0, y: yPosition },
            data: {
                id: `r${id}`,
                originalId: id,
                name,
                bottomYPos: yPosition + RECIPE_ITEM_HEIGHT,
                internalType: ScenarioNodeType.RECIPE,
                nodeWidth: widgetWidth / 4 - 64,
                shouldHighlightNode: highlightedIds.includes(id),
                isComplete,
            },
            sourcePosition: Position.Left,
            targetPosition: Position.Right,
        });
    });
    return recipesData;
};
const prepareSimulationsEdges = (simulations, highlightedIds, node) => {
    const edges = [];
    const isCellHighlighted = node?.entity === HighlightEntityType.CELL_MODEL;
    const isRecipeHighlighted = node?.entity === HighlightEntityType.RECIPE;
    simulations.forEach((simulation) => {
        const { id, cellModelId, recipeId } = simulation;
        const source = `s${id}`;
        const cellModelTarget = `c${cellModelId}`;
        const recipeTarget = `r${recipeId}`;
        if (cellModelId !== null) {
            edges.push({
                id: `${source}-${cellModelTarget}`,
                type: "edge",
                source,
                target: cellModelTarget,
                sourceHandle: `cs${id}`,
                data: {
                    shouldHighlight: isRecipeHighlighted ? false : highlightedIds.includes(id),
                },
            });
        }
        if (recipeId !== null) {
            edges.push({
                id: `${source}-${recipeTarget}`,
                type: "edge",
                source,
                target: recipeTarget,
                sourceHandle: `rs${id}`,
                data: {
                    shouldHighlight: isCellHighlighted ? false : highlightedIds.includes(id),
                },
            });
        }
    });
    return edges.sort((a, b) => a.data.shouldHighlight - b.data.shouldHighlight);
};
export const positionScenarioSimulationsAndProcessData = (simulations, processData, widgetWidth, availableHeight, simulationIds, processDataIds) => {
    const simulationsRequiredHeight = (SIMULATION_ITEM_HEIGHT + BOTTOM_MARGIN) * simulations.length;
    const processDataRequiredHeight = (PROCESS_DATA_ITEM_HEIGHT + BOTTOM_MARGIN) * processData.length;
    const requiredHeight = simulationsRequiredHeight + processDataRequiredHeight;
    const simulationSectionTopPadding = (availableHeight - requiredHeight) / 2 + BOTTOM_MARGIN / 2;
    const data = [];
    simulations
        .map(({ id, name }) => ({ id, name, type: ScenarioNodeType.SIMULATION }))
        .concat(processData.map(({ id, name }) => ({
        id,
        name,
        isCompleted: true,
        type: ScenarioNodeType.PROCESS_DATA,
    })))
        .forEach((item, index) => {
        const { id, name, type } = item;
        const previousPreparedNode = data[index - 1];
        const yPosition = !!previousPreparedNode && !!previousPreparedNode.data
            ? previousPreparedNode.data.bottomYPos + BOTTOM_MARGIN
            : simulationSectionTopPadding;
        const sectionWidth = widgetWidth / 4;
        const nodeWidth = sectionWidth - 110;
        const nodeId = `${type === ScenarioNodeType.SIMULATION ? "s" : "pd"}${id}`;
        const shouldHighlightNode = (type === ScenarioNodeType.SIMULATION ? simulationIds : processDataIds).includes(id);
        const simulationEntity = type === ScenarioNodeType.SIMULATION && simulations?.find((simulation) => simulation.id === id);
        const nodeHeight = simulationEntity ? SIMULATION_ITEM_HEIGHT : PROCESS_DATA_ITEM_HEIGHT;
        data.push({
            id: nodeId,
            type,
            position: { x: sectionWidth * 2 - nodeWidth - NODE_SIDE_MARGIN, y: yPosition },
            data: {
                id: nodeId,
                originalId: id,
                name,
                nodeWidth,
                internalType: type,
                bottomYPos: yPosition + nodeHeight,
                shouldHighlightNode,
            },
            sourcePosition: Position.Left,
            targetPosition: Position.Right,
        });
    });
    return data;
};
const prepareInsightsEdges = (insights, insightIds, simulationsIds, processDataIds) => {
    const edges = [];
    for (const insight of insights) {
        const { id, state } = insight;
        const inputs = state.input.inputSets.reduce((acc, inputSet) => {
            const filledInputs = inputSet.inputs.filter((item) => {
                const alreadyExist = acc.some((input) => input.id === item.id && input.inputId === item.inputId && input.inputType === item.inputType);
                return (item.inputId !== null &&
                    [InsightInputType.SIMULATION, InsightInputType.PROCESS_DATA].includes(item.inputType) &&
                    !alreadyExist);
            });
            return [...acc, ...filledInputs];
        }, []);
        for (const input of inputs) {
            const targetKey = input.inputType === InsightInputType.SIMULATION ? "s" : "pd";
            const sourceHandleKey = input.inputType === InsightInputType.SIMULATION ? "ss" : "pdi";
            const source = `i${id}`;
            const target = `${targetKey}${input.inputId}`;
            const alreadyExist = edges.some((item) => item.id === `${source}-${target}`);
            const isHighlighted = input.inputId &&
                (input.inputType === InsightInputType.SIMULATION ? simulationsIds : processDataIds).includes(input.inputId);
            const isInsightHighlighted = insightIds.includes(id);
            if (!alreadyExist) {
                edges.push({
                    id: `${source}-${target}`,
                    type: "edge",
                    source,
                    target: target,
                    sourceHandle: `${sourceHandleKey}${id}`,
                    data: {
                        shouldHighlight: isHighlighted && isInsightHighlighted,
                    },
                });
            }
        }
    }
    return edges;
};
export const positionScenarioInsights = (insights, widgetWidth, availableHeight, highlightedInsightIds) => {
    const topPadding = calculateCenteredSectionTopPadding(availableHeight, insights.length, INSIGHT_ITEM_HEIGHT);
    const insightsData = [];
    insights.forEach((insight, index) => {
        if (insight === null) {
            return;
        }
        const { id, name } = insight;
        const previousPreparedNode = insightsData[index - 1];
        const yPosition = !!previousPreparedNode && !!previousPreparedNode.data
            ? previousPreparedNode.data.bottomYPos + BOTTOM_MARGIN
            : topPadding;
        const sectionWidth = widgetWidth / 4;
        const nodeWidth = sectionWidth - 100;
        insightsData.push({
            id: `i${id}`,
            type: ScenarioNodeType.INSIGHT,
            position: { x: sectionWidth * 3 - nodeWidth - NODE_SIDE_MARGIN, y: yPosition },
            data: {
                id: `i${id}`,
                originalId: id,
                name,
                nodeWidth,
                internalType: ScenarioNodeType.INSIGHT,
                bottomYPos: yPosition + INSIGHT_ITEM_HEIGHT,
                shouldHighlightNode: highlightedInsightIds.includes(id),
            },
            sourcePosition: Position.Left,
            targetPosition: Position.Right,
        });
    });
    return insightsData;
};
const prepareOptimizationsEdges = (optimizations, highlightedIds) => {
    const edges = [];
    for (const optimization of optimizations) {
        const { id, state } = optimization;
        const source = `o${id}`;
        const target = `i${state.input?.insightId}`;
        if (isNotEmpty(state.input?.insightId)) {
            edges.push({
                id: `${source}-${target}`,
                type: "edge",
                source,
                target,
                sourceHandle: `oi${id}`,
                data: {
                    shouldHighlight: highlightedIds.includes(id),
                },
            });
        }
    }
    return edges;
};
export const positionScenarioOptimizations = (optimizations, widgetWidth, availableHeight, highlightedIds, insights, processVariables, incompleteSimulationIds, simulations, cellModels, recipes) => {
    const topPadding = calculateCenteredSectionTopPadding(availableHeight, optimizations.length, OPTIMIZATION_ITEM_HEIGHT);
    return optimizations.reduce((acc, item, index) => {
        if (!isNotEmpty(item)) {
            return acc;
        }
        const { id, name } = item;
        const previousPreparedNode = acc[index - 1];
        const yPosition = !!previousPreparedNode && !!previousPreparedNode.data
            ? previousPreparedNode.data.bottomYPos + BOTTOM_MARGIN
            : topPadding;
        const sectionWidth = widgetWidth / 4;
        const nodeWidth = sectionWidth - 100;
        const isComplete = isOptimizationCompleted(item.state, insights, processVariables, incompleteSimulationIds, simulations, cellModels, recipes);
        const preparedItem = {
            id: `o${id}`,
            type: ScenarioNodeType.OPTIMIZATION,
            position: { x: sectionWidth * 4 - nodeWidth - NODE_SIDE_MARGIN, y: yPosition },
            data: {
                id: `o${id}`,
                originalId: id,
                name: name ?? "",
                nodeWidth,
                internalType: ScenarioNodeType.OPTIMIZATION,
                bottomYPos: yPosition + OPTIMIZATION_ITEM_HEIGHT,
                isComplete,
                shouldHighlightNode: highlightedIds.includes(id),
            },
            sourcePosition: Position.Left,
            targetPosition: Position.Right,
        };
        return [...acc, preparedItem];
    }, []);
};
export const calculateAvailableHeight = (cellModels, recipes, simulations, insights, optimizations) => {
    const defaultAvailableHeight = window.innerHeight - 1 - PAGE_HEADERS_HEIGHT;
    const { cellModelsRecipesSectionHeight } = calculateScenarioCellModelsRecipesSectionHeight(cellModels, recipes, defaultAvailableHeight);
    const simulationsSectionHeight = calculateScenarioSimulationsSectionHeight(simulations, defaultAvailableHeight);
    const insightsSectionHeight = calculateScenarioInsightsSectionHeight(insights, defaultAvailableHeight);
    const optimizationsSectionHeight = calculateScenarioOptimizationsSectionHeight(optimizations, defaultAvailableHeight);
    return Math.max(cellModelsRecipesSectionHeight, simulationsSectionHeight, insightsSectionHeight, optimizationsSectionHeight);
};
export const calculateHighlightedElementIds = (simulations, processData, insights, optimizations, node) => {
    const cellModelIds = [];
    const recipeIds = [];
    const simulationIds = [];
    const variantIds = [];
    const processDataIds = [];
    const insightIds = [];
    const optimizationIds = [];
    if (!node) {
        return { cellModelIds, recipeIds, simulationIds, processDataIds, insightIds, optimizationIds };
    }
    const { id, entity, inputSetId, variantId } = node;
    switch (entity) {
        case HighlightEntityType.CELL_MODEL:
        case HighlightEntityType.RECIPE: {
            const isCellModel = entity === HighlightEntityType.CELL_MODEL;
            isCellModel ? cellModelIds.push(id) : recipeIds.push(id);
            const relatedSimulations = simulations.filter(({ cellModelId, recipeId }) => isCellModel ? cellModelId === id : recipeId === id);
            simulationIds.push(...relatedSimulations.map(({ id }) => id));
            const relatedInputSetIds = [];
            for (const insight of insights) {
                for (const inputSet of insight.state.input.inputSets) {
                    inputSet.inputs.filter(({ id, inputId, inputType }) => {
                        if (id &&
                            inputId &&
                            simulationIds.includes(inputId) &&
                            inputType === InsightInputType.SIMULATION) {
                            insightIds.push(insight.id);
                            relatedInputSetIds.push(inputSet.id);
                        }
                    });
                }
            }
            const usedOptimizations = optimizations.filter(({ state }) => state.input?.insightId &&
                insightIds.includes(state.input?.insightId) &&
                state.input?.insightInputSetId &&
                relatedInputSetIds.includes(state.input?.insightInputSetId));
            optimizationIds.push(...usedOptimizations.map((item) => item.id));
            break;
        }
        case HighlightEntityType.SIMULATION: {
            simulationIds.push(id);
            variantId !== null && variantIds.push(variantId);
            const cellRelatedSimulations = simulations.filter((item) => item.id === id && item.cellModelId);
            const recipeRelatedSimulations = simulations.filter((item) => item.id === id && item.recipeId);
            cellModelIds.push(...cellRelatedSimulations.map(({ cellModelId }) => cellModelId));
            recipeIds.push(...recipeRelatedSimulations.map(({ recipeId }) => recipeId));
            const insightRelatedInputSetIds = [];
            for (const insight of insights) {
                for (const inputSet of insight.state.input.inputSets) {
                    inputSet.inputs.forEach(({ id, inputId, inputType, adjustmentId }) => {
                        if (id &&
                            inputId &&
                            isNotEmpty(adjustmentId) &&
                            simulationIds.includes(inputId) &&
                            inputType === InsightInputType.SIMULATION &&
                            adjustmentId === variantId) {
                            insightIds.push(insight.id);
                            insightRelatedInputSetIds.push(inputSet.id);
                        }
                    });
                }
            }
            const relatedOptimizations = optimizations.filter((item) => item.state.input?.insightId &&
                insightIds.includes(item.state.input?.insightId) &&
                item.state.input?.insightInputSetId &&
                insightRelatedInputSetIds.includes(item.state.input?.insightInputSetId));
            optimizationIds.push(...relatedOptimizations.map((item) => item.id));
            break;
        }
        case HighlightEntityType.PROCESS_DATA: {
            processDataIds.push(id);
            const insightRelatedProcessDataIds = [];
            for (const insight of insights) {
                for (const inputSet of insight.state.input.inputSets) {
                    inputSet.inputs.forEach(({ id, inputId, inputType }) => {
                        if (id &&
                            inputId &&
                            processDataIds.includes(inputId) &&
                            inputType === InsightInputType.PROCESS_DATA) {
                            insightIds.push(insight.id);
                            insightRelatedProcessDataIds.push(inputSet.id);
                        }
                    });
                }
            }
            const optimizationsRelatedToProcessData = optimizations.filter((item) => item.state.input?.insightId &&
                insightIds.includes(item.state.input?.insightId) &&
                item.state.input?.insightInputSetId &&
                insightRelatedProcessDataIds.includes(item.state.input?.insightInputSetId));
            optimizationIds.push(...optimizationsRelatedToProcessData.map((item) => item.id));
            break;
        }
        case HighlightEntityType.INSIGHT: {
            insightIds.push(id);
            const insightRelatedOptimizations = optimizations.filter((item) => inputSetId
                ? item.state.input?.insightId === id && item.state.input?.insightInputSetId === inputSetId
                : item.state.input?.insightId === id);
            optimizationIds.push(...insightRelatedOptimizations.map(({ id }) => id));
            const currentInsight = insights.find((item) => item.id === id);
            if (currentInsight?.state.input.inputSets) {
                for (const inputSet of currentInsight?.state.input.inputSets) {
                    if (inputSet.id === inputSetId) {
                        inputSet.inputs.forEach(({ inputId, inputType, adjustmentId }) => {
                            if (inputType === InsightInputType.SIMULATION && inputId) {
                                simulationIds.push(inputId);
                            }
                            if (inputType === InsightInputType.SIMULATION &&
                                isNotEmpty(adjustmentId) &&
                                adjustmentId !== null) {
                                variantIds.push(adjustmentId);
                            }
                            if (inputType === InsightInputType.PROCESS_DATA && inputId) {
                                processDataIds.push(inputId);
                            }
                        });
                    }
                }
            }
            for (const simulation of simulations) {
                if (simulationIds.includes(simulation.id)) {
                    cellModelIds.push(simulation.cellModelId);
                    recipeIds.push(simulation.recipeId);
                }
            }
            break;
        }
        case HighlightEntityType.OPTIMIZATION: {
            optimizationIds.push(id);
            const currentOptimizationInput = optimizations.find((item) => item.id === id)?.state.input;
            if (currentOptimizationInput && currentOptimizationInput.insightId) {
                insightIds.push(currentOptimizationInput.insightId);
            }
            const relatedInsights = insights.filter((item) => insightIds.includes(item.id));
            for (const insight of relatedInsights) {
                if (insight.state.input.inputSets) {
                    for (const inputSet of insight.state.input.inputSets) {
                        inputSet.inputs.forEach(({ inputId, inputType, adjustmentId }) => {
                            if (inputType === InsightInputType.SIMULATION && inputId) {
                                simulationIds.push(inputId);
                            }
                            if (inputType === InsightInputType.SIMULATION &&
                                isNotEmpty(adjustmentId) &&
                                adjustmentId !== null) {
                                variantIds.push(adjustmentId);
                            }
                            if (inputType === InsightInputType.PROCESS_DATA && inputId) {
                                processDataIds.push(inputId);
                            }
                        });
                    }
                }
            }
            for (const simulation of simulations) {
                if (simulationIds.includes(simulation.id)) {
                    cellModelIds.push(simulation.cellModelId);
                    recipeIds.push(simulation.recipeId);
                }
            }
            break;
        }
    }
    return {
        cellModelIds,
        recipeIds,
        simulationIds,
        processDataIds,
        insightIds: [...new Set(insightIds)],
        optimizationIds,
        variantIds: [...new Set(variantIds ?? [])],
    };
};
export const positionScenarioCanvasElements = (cellModels, recipes, simulations, insights, optimizations, processData, widgetWidth, availableHeight, highlightLeadingNode, processVariables, incompleteSimulationIds) => {
    const { cellModelIds, recipeIds, simulationIds, processDataIds, insightIds, optimizationIds } = calculateHighlightedElementIds(simulations, processData, insights, optimizations, highlightLeadingNode);
    const cellModelsData = positionScenarioCellModels(cellModels, recipes, widgetWidth, availableHeight, cellModelIds);
    const recipesData = positionScenarioRecipes(recipes, cellModels, widgetWidth, availableHeight, recipeIds);
    const simulationsAndProcessData = positionScenarioSimulationsAndProcessData(simulations, processData, widgetWidth, availableHeight, simulationIds, processDataIds);
    const insightsData = positionScenarioInsights(insights, widgetWidth, availableHeight, insightIds);
    const optimizationsData = positionScenarioOptimizations(optimizations, widgetWidth, availableHeight, optimizationIds, insights, processVariables, incompleteSimulationIds, simulations, cellModels, recipes);
    const simulationsEdges = prepareSimulationsEdges(simulations, simulationIds, highlightLeadingNode);
    const insightsEdges = prepareInsightsEdges(insights, insightIds, simulationIds, processDataIds);
    const optimizationsEdges = prepareOptimizationsEdges(optimizations, optimizationIds);
    return {
        nodes: [
            ...cellModelsData,
            ...recipesData,
            ...simulationsAndProcessData,
            ...insightsData,
            ...optimizationsData,
        ],
        edges: [...simulationsEdges, ...insightsEdges, ...optimizationsEdges],
    };
};
export const prepareCurrentSimulationPreviewLinesData = (selectedVariables, values) => {
    if (!isNotEmpty(values) || values?.length === 0) {
        return [];
    }
    const dataLayout = {
        type: "scatter",
        mode: "lines",
    };
    const currentGraphLines = selectedVariables.map((variable) => {
        const lineData = values?.find((item) => item.variable === variable?.value);
        return lineData?.points || [];
    });
    const { max: maxDuration } = getArrayMinMax((currentGraphLines[0] ?? []).map((item) => item.x) ?? []);
    const unit = getTimeUnit(maxDuration);
    const lines = currentGraphLines.map((points, index) => ({
        ...dataLayout,
        line: {
            color: PREVIEW_LINE_COLORS?.[index],
            width: 3,
        },
        ...(index === 0 ? {} : { yaxis: `y${index + 1}` }),
        x: points.map((item) => isNaN(item.x) ? 0 : numberToFixedPointNotation(getSimulationPreviewXAxisPointValue(item.x, unit))),
        y: points.map((item) => (isNaN(item.y) ? undefined : numberToFixedPointNotation(item.y, 9))),
    }));
    return { lines, unit };
};
export const prepareGroupedVariablesList = (values) => {
    let options = [];
    const groups = Array.from(new Set(values.map((item) => item.groupName)));
    groups.forEach((group) => {
        const groupVariables = values
            .filter((item) => item.groupName === group)
            .map((item) => {
            const isComplexUnit = item?.unitsResult?.category === ParameterTypesCategoryType.Custom;
            const isNoUnits = item?.unitsResult?.category === ParameterTypesCategoryType.NoUnits;
            const simpleUnitValue = item?.unitsResult?.value;
            let unit = "";
            if (!isComplexUnit && !isNoUnits) {
                unit = findDictionaryItemByValue(recipeParameterUnitsType, simpleUnitValue)?.label ?? "";
            }
            if (isComplexUnit) {
                unit = parseComplexUnitToHtml(item?.unitsResult?.complexValue);
            }
            return {
                value: item.variable,
                label: item.variable,
                isGroupName: false,
                unit,
            };
        });
        const bulk = [{ value: group, label: group, isGroupName: true }, ...groupVariables];
        options = [...options, ...bulk];
    });
    return options;
};
export const prepareSimulationPreviewExportedData = (simulationData) => {
    const { data = [] } = { ...simulationData?.graphData };
    const headers = [
        "Output interval",
        ...data.map(({ variable, unitsResult, groupName }) => {
            let unitsTextContent = "";
            const isComplexUnit = unitsResult?.category === ParameterTypesCategoryType.Custom;
            const isNoUnits = unitsResult?.category === ParameterTypesCategoryType.NoUnits;
            const simpleUnitValue = unitsResult?.value;
            if (!isComplexUnit && !isNoUnits) {
                unitsTextContent = `(${getTextContentFromHtml(findDictionaryItemByValue(recipeParameterUnitsType, simpleUnitValue)?.label)}) `;
            }
            if (isComplexUnit) {
                const unitHtml = parseComplexUnitToHtml(unitsResult?.complexValue);
                unitsTextContent = `(${unitHtml?.rawUnitString}) `;
            }
            return `${variable} ${unitsTextContent}${groupName}`;
        }),
    ];
    const rows = [];
    const timeIntervals = (data?.[0]?.points ?? []).map((point) => point.x);
    timeIntervals.forEach((interval) => {
        const intervalResults = data?.map((item) => {
            const point = item.points.find((point) => point.x === interval);
            return (point?.y ?? 0).toString();
        });
        rows.push([interval.toString(), ...intervalResults]);
    });
    return convertToCSV([headers, ...rows]);
};
export const getSimulationPreviewXAxisPointValue = (duration, unit) => {
    if (unit === TimeUnits.seconds) {
        return duration;
    }
    if (unit === TimeUnits.minutes) {
        return duration / 60;
    }
    if (unit === TimeUnits.hours) {
        return duration / 3600;
    }
    return duration / 86400;
};
export const hasInsightScoreToOptimize = (optimizationInput, insights, persistedInsightsData) => {
    const selectedInsight = insights.find(({ id }) => id === optimizationInput?.insightId);
    const selectedInsightPersistedData = persistedInsightsData?.find(({ insightId }) => insightId === optimizationInput?.insightId) ||
        {};
    if (!selectedInsight || !selectedInsightPersistedData) {
        return false;
    }
    if (optimizationInput?.outputId === OUTPUT_FINAL_ID) {
        const outputsWithScoresIds = selectedInsightPersistedData?.scores
            ?.filter(({ inputSetId }) => inputSetId === optimizationInput?.insightInputSetId)
            ?.map(({ outputId }) => outputId);
        return outputsWithScoresIds?.length === selectedInsight?.state?.outputs?.length;
    }
    else {
        const scoreForOutput = selectedInsightPersistedData.scores?.find(({ outputId }) => outputId === optimizationInput?.outputId);
        return isNotEmpty(scoreForOutput);
    }
};
export const getSimulationSourceName = (simulations, optimizations, simulationId, optimizationId, adjustmentId, customSeparator) => {
    const simulation = simulations?.find(({ id }) => id === simulationId);
    const optimization = optimizations?.find(({ id }) => id === optimizationId);
    const variant = simulation?.state?.adjustments?.find(({ id }) => id === adjustmentId);
    const separator = customSeparator ?? "_";
    let sourceName = optimization ? `${optimization?.name}${separator}` : "";
    if (simulation) {
        sourceName += simulation?.name || "";
    }
    if (!!variant && variant?.id !== 0) {
        sourceName += `${separator}${variant?.name}`;
    }
    return sourceName;
};
export const getInsightSourceName = (insights, persistedInsightsData, processVariables, insightId, inputSetId, output, recipes, customSeparator) => {
    const separator = customSeparator ?? "_";
    const outputId = output?.isFinal ? OUTPUT_FINAL_ID : (output?.id ?? undefined);
    const outputOptions = getInsightOutputOptions(insightId, inputSetId, outputId, insights, persistedInsightsData, processVariables, recipes);
    const outputName = outputOptions.find(({ value }) => value === outputId)?.label;
    const insightName = insights?.find(({ id }) => id === insightId)?.name;
    return insightName ? `${insightName}${separator}Input set ${inputSetId}${separator}${outputName}` : "";
};
export const getOptimizationInputsNames = (simulations = [], processDatas = [], insight, insightInputSetId, outputId) => {
    const selectedInputSet = insight?.state?.input?.inputSets?.find(({ id }) => id === insightInputSetId);
    const inputsToDisplay = [];
    const setInputToDisplay = (input) => {
        if (!inputsToDisplay.find(({ inputType, id }) => inputType === input.inputType && id === input.id)) {
            inputsToDisplay.push(input);
        }
    };
    if (outputId === OUTPUT_FINAL_ID) {
        insight?.state?.outputs?.forEach(({ sourceA, sourceB }) => {
            selectedInputSet?.inputs?.forEach((input) => (input.id === sourceA || input.id === sourceB) && setInputToDisplay(input));
        });
    }
    else {
        const selectedOutput = insight?.state?.outputs?.find(({ id }) => id === outputId);
        selectedInputSet?.inputs?.forEach((input) => (input.id === selectedOutput?.sourceA || input.id === selectedOutput?.sourceB) &&
            setInputToDisplay(input));
    }
    const processDatasItems = inputsToDisplay.reduce((acc, { inputType, inputId }, index) => {
        if (inputType !== InsightInputType.PROCESS_DATA) {
            return acc;
        }
        const selectedProcessData = processDatas.find((processData) => inputId === processData.id);
        if (selectedProcessData) {
            return [...acc, { name: selectedProcessData?.name ?? "", key: index }];
        }
        return acc;
    }, []);
    return [
        ...sortBy(inputsToDisplay, "id").reduce((acc, { id, inputType, inputId, adjustmentId }, index) => {
            if (inputType !== InsightInputType.SIMULATION) {
                return acc;
            }
            const selectedSimulation = simulations.find((simulation) => inputId === simulation.id);
            if (selectedSimulation) {
                const variant = selectedSimulation?.state?.adjustments?.find(({ id }) => id === adjustmentId);
                const name = !!variant && variant?.id !== 0
                    ? `${id}. ${selectedSimulation?.name}_${variant?.name}`
                    : `${id}. ${selectedSimulation?.name}`;
                return [...acc, { name, key: index }];
            }
            return acc;
        }, []),
        ...processDatasItems,
    ];
};
export const prepareParametersOnChangeOutput = (insightId, insightInputSetId, outputId, currentParameters = [], insights, simulations) => {
    const selectedInsight = insights.find(({ id }) => id === insightId);
    const selectedInputSet = selectedInsight?.state?.input?.inputSets?.find(({ id }) => id === insightInputSetId);
    let inputsToRenderParameters = [];
    if (outputId === OUTPUT_FINAL_ID) {
        const existedTargetSourcesIds = [];
        inputsToRenderParameters =
            selectedInsight?.state?.outputs?.reduce((acc, { sourceA, sourceB }) => {
                const selectedInputs = selectedInputSet?.inputs?.reduce((acc, input) => input.id === sourceA || input.id === sourceB ? [...acc, input] : acc, []) ?? [];
                selectedInputs.forEach((selectedInput) => {
                    const targetSource = getOptimizationParameterSectionTargetSource(selectedInput, simulations);
                    if (targetSource !== undefined) {
                        const targetSourceId = `${targetSource.sourceId}-${targetSource.adjustmentId}`;
                        if (!existedTargetSourcesIds.includes(targetSourceId)) {
                            existedTargetSourcesIds.push(targetSourceId);
                            const parametersSectionIndex = currentParameters.findIndex((section) => section?.targetSource?.sourceId === targetSource.sourceId &&
                                section?.targetSource?.adjustmentId === targetSource.adjustmentId);
                            const parameters = parametersSectionIndex === -1 ? [] : currentParameters[parametersSectionIndex].parameters;
                            acc.push({
                                inputId: selectedInput?.id,
                                targetSource: {
                                    sourceId: targetSource.sourceId,
                                    adjustmentId: targetSource.adjustmentId,
                                },
                                parameters,
                            });
                        }
                    }
                });
                return acc;
            }, []) ?? [];
    }
    else if (isNotEmpty(outputId)) {
        const selectedOutput = selectedInsight?.state?.outputs?.find(({ id }) => id === outputId);
        const existedTargetSourcesIds = [];
        selectedInputSet?.inputs?.forEach((input) => {
            if (input.id === selectedOutput?.sourceA || input.id === selectedOutput?.sourceB) {
                const targetSource = getOptimizationParameterSectionTargetSource(input, simulations);
                if (targetSource !== undefined) {
                    const targetSourceId = `${targetSource.sourceId}-${targetSource.adjustmentId}`;
                    if (!existedTargetSourcesIds.includes(targetSourceId)) {
                        existedTargetSourcesIds.push(targetSourceId);
                        const parametersSectionIndex = currentParameters.findIndex((section) => section?.targetSource?.sourceId === targetSource.sourceId &&
                            section?.targetSource?.adjustmentId === targetSource.adjustmentId);
                        const parameters = parametersSectionIndex === -1 ? [] : currentParameters[parametersSectionIndex].parameters;
                        inputsToRenderParameters.push({
                            inputId: input?.id,
                            targetSource: {
                                sourceId: targetSource.sourceId,
                                adjustmentId: targetSource.adjustmentId,
                            },
                            parameters,
                        });
                    }
                }
            }
        });
    }
    return Array.from(Array(3), (_, index) => inputsToRenderParameters[index] ?? { parameters: [] });
};
export const setupAlternativeStepOptimizationParameter = (parameter, target) => {
    const { stepId, flowIds } = parameter;
    const usedStep = (flatSteps(target?.state.steps || []).find(({ id, type }) => type === RecipeStepType.ALTERNATIVE && id === stepId) || {});
    return {
        ...parameter,
        [ParameterFormFields._flowIdsMapping]: usedStep.flowProperties?.map(({ id }) => ({ id })),
        [ParameterFormFields._flowIdsSelection]: usedStep.flowProperties?.map(({ id }) => !!flowIds?.includes(id)),
    };
};
export const compareParametersToOptimize = (oldParameters, newParameters) => {
    const parameterNormalizer = (parameter) => {
        switch (parameter.type) {
            case OptimizationParameterParameterType.CELL_MODEL_OR_RECIPE: {
                const { parameterId, min, max } = { ...parameter };
                return {
                    type: parameter.type,
                    parameterId: `${parameterId}`,
                    min: parseFloat(`${min}`),
                    max: parseFloat(`${max}`),
                };
            }
            case OptimizationParameterParameterType.ALTERNATIVE_FLOW: {
                const { stepId, flowIds } = { ...parameter };
                return {
                    type: parameter.type,
                    stepId: Number(stepId),
                    flowIds,
                };
            }
            default:
                return {};
        }
    };
    return isEqual(oldParameters?.map(({ parameters }) => parameters?.map((parameter) => parameterNormalizer(parameter))), newParameters?.map(({ parameters }) => parameters?.map((parameter) => parameterNormalizer(parameter))));
};
export const getSimulationRemoveConfirmationWarning = (relatedInsightsInputs, hasSimulationRelatedResults) => {
    if (relatedInsightsInputs.length && hasSimulationRelatedResults) {
        return DELETE_SCENARIO_ENTITY_MESSAGES.REMOVE_SIMULATION_INCLUDED_INSIGHTS_AND_RESULTS;
    }
    if (relatedInsightsInputs.length) {
        return DELETE_SCENARIO_ENTITY_MESSAGES.REMOVE_SIMULATION_INCLUDED_INPUTS;
    }
    if (hasSimulationRelatedResults) {
        return DELETE_SCENARIO_ENTITY_MESSAGES.REMOVE_SIMULATION_INCLUDED_RESULTS;
    }
};
export const GET_ALL_CUSTOM_VARIABLES = "GET_ALL_CUSTOM_VARIABLES";
export const getRecipesCustomVariables = (recipes, recipeIds, filterFn) => (recipeIds === GET_ALL_CUSTOM_VARIABLES ? recipes.map(({ id }) => id) : recipeIds)?.reduce((acc, recipeId) => {
    const recipe = recipes.find(({ id }) => id === recipeId);
    let customVariables = recipe?.state?.customVariables || [];
    if (filterFn) {
        customVariables = customVariables.filter(filterFn);
    }
    customVariables = customVariables.map((customVariable) => ({
        ...customVariable,
        recipeId,
    }));
    return Array.from(new Set([...acc, ...customVariables]));
}, []) ?? [];
export const mergeSpaceviewerResults = (responses, parameters) => {
    if (responses.length === 1 && responses[0].alternativeFlows.length === 0) {
        return responses[0]?.result ?? { values: [], value: 0 };
    }
    const values = [];
    let value = responses[0]?.result?.value ?? 0;
    responses.forEach(({ alternativeFlows, result }) => {
        const valuesTemplate = new Array(parameters.length).fill(null);
        alternativeFlows.forEach(({ simulationId, adjustmentId, config }) => {
            const parameterIndex = parameters.findIndex((parameter) => parameter.type === SpaceviewerAxisType.INTEGER &&
                parameter.id === config.stepId &&
                parameter.simulationId === simulationId &&
                parameter.adjustmentId === adjustmentId);
            const valueIndex = (parameters[parameterIndex]?.flowIds || []).findIndex((id) => id === config.flowId);
            valuesTemplate[parameterIndex] =
                parameters[parameterIndex]?.steps[valueIndex]?.value ?? null;
        });
        result?.values.forEach(({ values: valuesValues, response }) => {
            let counter = 0;
            const expandedValues = [...valuesTemplate];
            for (const valueIndex in expandedValues) {
                if (expandedValues[valueIndex] === null) {
                    expandedValues[valueIndex] = valuesValues[counter];
                    counter++;
                }
            }
            values.push({ response, values: expandedValues });
        });
        if ((result?.value ?? 0) > value) {
            value = result?.value ?? 0;
        }
    });
    return { values, value };
};
export const hasOptimizationUsingGivenVariant = (simulationId, variantId, optimizations, insights) => optimizations.some((optimization) => {
    const { insightId, insightInputSetId } = optimization?.state?.input ?? {};
    const inputs = insights
        .find(({ id }) => insightId === id)
        ?.state.input.inputSets.find(({ id }) => id === insightInputSetId)?.inputs;
    return inputs?.some(({ inputId, inputType, adjustmentId }) => inputType === InsightInputType.SIMULATION && inputId === simulationId && adjustmentId === variantId);
});
export const composeSimulationErrorMessage = (error) => {
    if (!error) {
        return "";
    }
    const { recipeError } = { ...error };
    const { d, h, m, s } = getTimePartsFromSeconds(recipeError?.time ?? 0);
    return `A problem occurred in the Simulation at ${d > 0 ? `Day ${d}, ` : ""}${[h, m, s]
        .map((num) => toZeroPrefixNotation(num))
        .join(":")}. ${recipeError?.step ? `Step: ${recipeError?.step}. ` : ""}Reason: ${recipeError?.reason}. Please update the Recipe to enable the Simulation to complete successfully.`;
};
